import { DataTableSkeleton } from '@/components/ui/table/data-table-skeleton';
import { columns } from '@/modules/data/category-data-table/columns';
import { DataTable } from '@/components/ui/table/data-table';
import { useGetMapCategories } from '@/services/mapdata/queries';
import CategoryDataTableAction from '@/modules/data/category-data-table/data-table-action';
import { useCategoryDataFilters } from '@/modules/data/category-data-table/use-category-data-filters';
import { useAuthStore } from '@/store/auth';
import { Database } from 'lucide-react';
import PageContainer from '@/components/layout/page-container';
import { Heading } from '@/components/ui/heading';
import { useMemo } from 'react';

export default function DataPage() {
  const activeWorkspace = useAuthStore((state) => state.activeWorkspace);
  const { searchQuery, sort } = useCategoryDataFilters();

  // Memoize filters to prevent unnecessary re-renders
  const filters = useMemo(() => ({
    page: 1, // Default page
    limit: 20, // Default limit - must match DataTable pageSize
    ...(searchQuery && { search: searchQuery }),
    ...(sort && { sort })
  }), [searchQuery, sort]);


  const { data, isLoading, error } = useGetMapCategories(
    activeWorkspace || '',
    filters,
    {
      staleTime: 1000 * 60 * 2, // 2 minutes
      refetchOnWindowFocus: true,
      refetchOnMount: true
    }
  );

  const tableData = data?.data || [];
  const total = data?.pagination?.totalDocs || 0;

  if (isLoading) return <DataTableSkeleton />;

  if (error) {
    return (
      <PageContainer scrollable={true}>
        <div className='flex h-64 items-center justify-center'>
          <p className='text-destructive'>Error loading categories</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer scrollable={true}>
      <div className='space-y-6'>
        {/* Header Section */}
        <div className='flex items-center justify-between'>
          <Heading 
            title="Data Categories" 
            description="Manage your data categories and items for the stakeholder map" 
          />
        </div>

        {/* Table Section */}
        <div className='space-y-4 bg-white py-4'>
          <CategoryDataTableAction data={tableData} />
          <div className='px-4'>
            <DataTable
              columns={columns}
              data={tableData}
              totalItems={total}
              emptyState={
                <div className='flex h-full w-full flex-col items-center justify-center py-12'>
                  <Database className='h-12 w-12 text-muted-foreground mb-4' />
                  <h3 className='text-lg font-semibold text-foreground mb-2'>No categories found</h3>
                  <p className='text-sm text-muted-foreground text-center max-w-sm'>
                    You can create a data category to organize your stakeholder information.
                  </p>
                </div>
              }
            />
          </div>
        </div>
      </div>
    </PageContainer>
  );
}